import { useGetApiClient } from '../../../context/apiContext';
import { RequestState, useRequestState } from '../../../hooks';

import type { getSearchHotelRooms } from '@lt/api/dist/methods/search/types';
import type { postHotelRoomsResponse } from '@lt/api/dist/methods/widgets/types';
import type { SearchParamsWithDates } from '../types';

type SetHotelRooms = (hotelRooms: postHotelRoomsResponse['result']) => void;
interface IFetchHotelRooms {
  setHotelRooms: SetHotelRooms;
  searchParams: SearchParamsWithDates;
  isInitial: boolean;
}

export const useFetchHotelRooms = (): ((params: IFetchHotelRooms) => Promise<void>) | undefined => {
  const api = useGetApiClient();
  const { setRequestState } = useRequestState();

  if (!api) {
    console.log('Не найден ключ партнёра');
    return;
  }

  const fetchHotelRooms = async (params: IFetchHotelRooms) => {
    const { setHotelRooms, searchParams /* , isInitial */ } = params;
    // const cachedRequestParams = getCachedRequestParams(searchParams);

    setRequestState(RequestState.LOADING);

    try {
      const { request_id } = await api.getSearchEnqueue(searchParams);
      const hotel_id = searchParams.hotel_ids.toString();
      const requestParams = { request_id, hotel_id };

      const response = await makeSearchHotelRoomsRequest(requestParams);
      // const response = await makeCachedHotelRoomsRequest(cachedRequestParams, isInitial);

      if (response.success /* && isInitial */) {
        handleSuccessfulResponse(response, setHotelRooms);
      } else {
        // const { request_id } = await api.getSearchEnqueue(cachedRequestParams);

        const timeoutResponse = await makeStatusRequest({
          request_id,
          getResponse: () => makeSearchHotelRoomsRequest(requestParams),
          // getResponse: () => makeCachedHotelRoomsRequest(cachedRequestParams, isInitial),
        });

        if (!timeoutResponse) return;
        handleSuccessfulResponse(timeoutResponse, setHotelRooms);
      }
    } catch (e) {
      console.log('Error', e);
      setRequestState(RequestState.ERROR);
    }
  };

  const makeSearchHotelRoomsRequest = async (params: getSearchHotelRooms) =>
    (await api.getSearchHotelRooms(params)) as postHotelRoomsResponse;

  interface IMakeStatusRequest {
    request_id: string;
    getResponse: () => Promise<postHotelRoomsResponse>;
  }

  const makeStatusRequest = async (
    params: IMakeStatusRequest
  ): Promise<postHotelRoomsResponse | void> => {
    const { request_id, getResponse } = params;

    const searchStartTime = Date.now();
    let isTimedOut = false;
    let completed = 0;
    let prevSize = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      isTimedOut = Date.now() - searchStartTime > 61000;

      try {
        await new Promise((r) => setTimeout(r, 1000));

        const { status, size } = await api.getSearchStatus({
          request_id,
        });
        const operatorsStatus = Object.values(status);

        const completedNext = operatorsStatus.filter(
          (item) => item === 'completed' || item === 'cached'
        ).length;

        if (completed !== completedNext || prevSize !== size) {
          const response = await getResponse();

          if (response.success) {
            return response;
          }
          setRequestState(RequestState.ERROR);
        }

        completed = completedNext;
        prevSize = size;

        if (
          operatorsStatus.every((st) => ['pending', 'performing'].indexOf(st) === -1) ||
          isTimedOut
        ) {
          break;
        }
      } catch (error) {
        console.log('Не удалось проверить статус ТО', error);
        break;
      }

      if (isTimedOut) {
        break;
      }
    }
  };

  const handleSuccessfulResponse = (
    response: postHotelRoomsResponse,
    setHotelRooms: SetHotelRooms
  ) => {
    const hotelRooms = pickHotelRoomsFromResponse(response);
    setHotelRooms(hotelRooms);

    setRequestState(RequestState.SUCCESS);
  };

  const pickHotelRoomsFromResponse = (
    response: postHotelRoomsResponse
  ): postHotelRoomsResponse['result'] =>
    response.result.map((roomInfo) => ({
      ...roomInfo,
      room: { ...roomInfo.room, images: roomInfo.room.images?.slice(0, 3) },
    }));

  /**
   * Метод для преобразования SearchParamsWithDates
   * в postHotelRoomsRequest['search_params'] для
   * вызова postWidgetsHotelRooms
   */
  // const getCachedRequestParams = (
  //   searchParams: SearchParamsWithDates
  // ): postHotelRoomsRequest['search_params'] =>
  //   ({
  //     ...pick(searchParams, [
  //       'from_city',
  //       'to_country',
  //       'start_date_from',
  //       'start_date_till',
  //       'end_date_from',
  //       'end_date_till',
  //       'adults',
  //       'kids',
  //       'search_type',
  //     ]),
  //     hotel_ids: [searchParams['hotel_ids']],
  //     ...('kids' in searchParams &&
  //       searchParams.kids > 0 && {
  //         kids_ages: new Array(searchParams.kids).fill(DEFAULT_KID_AGE),
  //       }),
  //   } as postHotelRoomsRequest['search_params']);

  // const makeCachedHotelRoomsRequest = async (
  //   search_params: postHotelRoomsRequest['search_params'],
  //   initial: boolean
  // ) =>
  //   await api.postWidgetsHotelRooms({
  //     search_params,
  //     locale: 'ru',
  //     currency: 'rub',
  //     initial,
  //   });

  return fetchHotelRooms;
};
